import axios from 'axios';
import { readStorageValue } from './utils/storage';

const googleTranslateSuffix = '.translate.goog';
const googleTranslateHost = 'translate.googleusercontent.com';

const decodeGoogleTranslateHost = (encodedHost = '') => {
  if (!encodedHost) {
    return '';
  }

  let decoded = '';
  for (let index = 0; index < encodedHost.length; index += 1) {
    const character = encodedHost[index];
    if (character === '-') {
      if (encodedHost[index + 1] === '-') {
        decoded += '-';
        index += 1;
      } else {
        decoded += '.';
      }
    } else {
      decoded += character;
    }
  }

  return decoded;
};

const isGoogleTranslateProxyHost = (hostname = '') => {
  if (!hostname) return false;
  const normalizedHost = String(hostname).toLowerCase();
  return (
    normalizedHost.endsWith(googleTranslateSuffix) || normalizedHost === googleTranslateHost
  );
};

const resolveTranslatedOrigin = (location) => {
  if (!location) {
    return null;
  }

  const { hostname, protocol, search } = location;

  if (hostname.endsWith(googleTranslateSuffix)) {
    const encodedHost = hostname.slice(0, -googleTranslateSuffix.length);
    const decodedHost = decodeGoogleTranslateHost(encodedHost);

    if (!decodedHost) {
      return null;
    }

    const params = new URLSearchParams(search);
    const scheme = params.get('_x_tr_sch');
    const translatedPort = params.get('_x_tr_port');
    const resolvedProtocol = scheme ? `${scheme}:` : protocol;
    const portSegment = translatedPort ? `:${translatedPort}` : '';

    return `${resolvedProtocol}//${decodedHost}${portSegment}`;
  }

  if (hostname === googleTranslateHost) {
    const params = new URLSearchParams(search);
    const originalUrl = params.get('u') || params.get('url');

    if (originalUrl) {
      try {
        const parsed = new URL(originalUrl);
        return parsed.origin;
      } catch (error) {
        return null;
      }
    }
  }

  return null;
};

const resolveBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window !== 'undefined') {
    const runtimeGlobal =
      window.__APP_API_URL__ || window.__API_BASE_URL__ || window.APP_API_BASE_URL;
    if (runtimeGlobal) {
      return runtimeGlobal;
    }

    const runtimeMeta =
      typeof document !== 'undefined'
        ? document.querySelector('meta[name="api-base-url"]')?.content
        : null;
    if (runtimeMeta) {
      return runtimeMeta;
    }

    const translatedOrigin = resolveTranslatedOrigin(window.location);
    if (translatedOrigin) {
      return translatedOrigin;
    }

    const { protocol, hostname, port } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const effectivePort = port || (isLocalhost ? '3001' : '');
    const portSegment = effectivePort ? `:${effectivePort}` : '';

    return `${protocol}//${hostname}${portSegment}`;
  }

  return 'http://localhost:3001';
};

const api = axios.create({
  baseURL: resolveBaseURL(),
});

const runningThroughGoogleTranslate =
  typeof window !== 'undefined' && isGoogleTranslateProxyHost(window.location?.hostname);

const extractTokenFromHeaderValue = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (/^Bearer\s+/i.test(trimmed)) {
    return trimmed.replace(/^Bearer\s+/i, '').trim();
  }

  return trimmed;
};

const resolveTokenFromHeaders = (headers) => {
  if (!headers) {
    return '';
  }

  try {
    if (typeof headers.get === 'function') {
      const headerValue = headers.get('Authorization') || headers.get('authorization');
      return extractTokenFromHeaderValue(headerValue);
    }
  } catch (error) {
    // Ignore headers access issues and fallback to direct property lookups
  }

  const directValue = headers.Authorization || headers.authorization;
  return extractTokenFromHeaderValue(directValue);
};

const getDefaultHeadersToken = () => {
  const defaults = api?.defaults?.headers;
  if (!defaults) {
    return '';
  }

  const common = typeof defaults.get === 'function' ? defaults : defaults.common;
  if (common) {
    const token = resolveTokenFromHeaders(common);
    if (token) {
      return token;
    }
  }

  return '';
};

const getEffectiveAuthToken = (config) => {
  const storedToken = readStorageValue('token');
  if (storedToken) {
    return storedToken;
  }

  const configToken = resolveTokenFromHeaders(config?.headers);
  if (configToken) {
    return configToken;
  }

  const defaultToken = getDefaultHeadersToken();
  if (defaultToken) {
    return defaultToken;
  }

  if (typeof window !== 'undefined' && window.__APP_AUTH_TOKEN__) {
    return String(window.__APP_AUTH_TOKEN__);
  }

  return '';
};

const ensureHeadersWithToken = (config, token) => {
  if (!config) return;
  const nextConfig = config;

  if (!nextConfig.headers) {
    nextConfig.headers = {};
  }

  const headers = nextConfig.headers;
  const hasHeader = (name) => {
    if (!headers) return false;
    if (typeof headers.has === 'function') {
      return headers.has(name) || headers.has(name.toLowerCase());
    }
    const normalized = name.toLowerCase();
    return (
      Object.prototype.hasOwnProperty.call(headers, name) ||
      Object.prototype.hasOwnProperty.call(headers, normalized)
    );
  };

  const setHeader = (name, value) => {
    if (!headers) return;
    if (typeof headers.set === 'function') {
      headers.set(name, value);
    } else {
      headers[name] = value;
    }
  };

  if (!hasHeader('Authorization')) {
    setHeader('Authorization', `Bearer ${token}`);
  }
  setHeader('x-authorization', token);
};

api.interceptors.request.use((config) => {
  if (typeof window === 'undefined') {
    return config;
  }

  const effectiveToken = getEffectiveAuthToken(config);

  if (!effectiveToken) {
    return config;
  }

  const nextConfig = { ...config };

  ensureHeadersWithToken(nextConfig, effectiveToken);

  if (runningThroughGoogleTranslate) {
    const ensureParamsWithToken = () => {
      if (nextConfig.params instanceof URLSearchParams) {
        if (!nextConfig.params.has('authToken')) {
          nextConfig.params.set('authToken', effectiveToken);
        }
        return true;
      }

      if (nextConfig.params && typeof nextConfig.params === 'object') {
        if (!Object.prototype.hasOwnProperty.call(nextConfig.params, 'authToken')) {
          nextConfig.params = { ...nextConfig.params, authToken: effectiveToken };
        }
        return true;
      }

      return false;
    };

    const ensureUrlWithToken = () => {
      if (typeof nextConfig.url !== 'string' || !nextConfig.url) {
        return false;
      }

      const [urlWithoutHash, hash = ''] = nextConfig.url.split('#');
      const [path, search = ''] = urlWithoutHash.split('?');
      const searchParams = new URLSearchParams(search);
      if (searchParams.has('authToken')) {
        return false;
      }
      searchParams.set('authToken', effectiveToken);
      const queryString = searchParams.toString();
      nextConfig.url = `${path}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
      return true;
    };

    if (!ensureParamsWithToken()) {
      if (!ensureUrlWithToken()) {
        nextConfig.params = { authToken: effectiveToken };
      }
    }
  }

  return nextConfig;
});

export default api;
